import type { Material, PerspectiveCamera, Scene, WebGLRenderer } from 'three'
import { Quaternion, ShaderMaterial, UniformsUtils, WebGLRenderTarget } from 'three'
import { FullScreenQuad } from 'three/examples/jsm/postprocessing/Pass'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass'
import { BILLBOARD_TYPE, Model } from '../model'
import { CUTOUT_NAME } from '../labels-manager'
import { DEBUG } from '@/lib/three-d-resources/debugger'

export const TransparencyShader = {
  name: 'TransparencyShader',

  uniforms: {
    tDiffuse: { type: 't', value: null },
    tOldScene: { type: 't', value: null },
    opacity: { type: 'f', value: 0.5 },
  },

  vertexShader: /* glsl */ `
    varying vec2 vUv;

    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
    }`,

  fragmentShader: /* glsl */ `
    uniform float opacity;
    uniform sampler2D tDiffuse;
    uniform sampler2D tOldScene;
    varying vec2 vUv;

    void main() {
      vec4 texel = texture2D( tDiffuse, vUv );
      vec4 oldTexel = texture2D(tOldScene, vUv);
      gl_FragColor = mix(texel, oldTexel, opacity);
    }`,
}

export class TransparencyPass extends RenderPass {
  private fsQuad: FullScreenQuad
  private uniforms: any
  // TODO please use this instead of the one in render (needs width and height to be adjusted with setSize)
  private oldSceneRender: WebGLRenderTarget
  material: Material

  constructor(scene: Scene, camera: PerspectiveCamera, width: number, height: number) {
    super(scene, camera)
    this.clear = false

    this.oldSceneRender = new WebGLRenderTarget(width, height)
    const shader = TransparencyShader
    this.uniforms = UniformsUtils.clone(shader.uniforms)
    this.material = new ShaderMaterial({
      transparent: false,
      opacity: 0.5,
      uniforms: this.uniforms,
      vertexShader: shader.vertexShader,
      fragmentShader: shader.fragmentShader,
    })
    this.fsQuad = new FullScreenQuad(this.material)
  }

  override render(renderer: WebGLRenderer, writeBuffer: WebGLRenderTarget, readBuffer: WebGLRenderTarget /*, deltaTime, maskActive */) {
    DEBUG.get().start('transparency_time')
    this.scene.traverse(child => {
      if (child.userData.type === BILLBOARD_TYPE) {
        const tempQuaternion = new Quaternion()
        if (child.parent) {
          child.parent.updateWorldMatrix(true, false)
          tempQuaternion.setFromRotationMatrix(child.parent.matrixWorld).invert()
        } else {
          tempQuaternion.set(0, 0, 0, 1) // Identity
        }

        tempQuaternion.multiply(this.camera.quaternion)
        child.quaternion.copy(tempQuaternion)
      }

      if (child.name === CUTOUT_NAME) {
        child.quaternion.copy(this.camera.quaternion)
      }
    })

    this.uniforms['tDiffuse'].value = readBuffer.texture

    const oldSceneRender = new WebGLRenderTarget(renderer.domElement.width, renderer.domElement.height)
    renderer.setRenderTarget(oldSceneRender)
    renderer.render(this.scene, this.camera)
    this.uniforms.tOldScene.value = oldSceneRender.texture

    renderer.setRenderTarget(writeBuffer)
    this.fsQuad.render(renderer)

    this.needsSwap = true
    oldSceneRender.dispose()
    DEBUG.get().end('transparency_time')
  }

  override dispose() {
    this.fsQuad.dispose()
    this.uniforms.tOldScene.value = null
    this.material.dispose()
  }

  override setSize() {
    // TODO: update rendertargets width height
  }

  setOpacity(value: number) {
    this.uniforms.opacity.value = value
  }
}
